import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Send, MessageCircle } from 'lucide-react'
import { MentionAutocomplete } from '@/components/ui/mention-autocomplete'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tag } from '@/state/types'
import { chatService } from '@/services'

export function ChatPage() {
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [tags, setTags] = useState<Tag[]>([])
  const navigate = useNavigate()

  const handleMentionAdd = (mention: Tag) => {
    setTags(prev => 
      prev.some(t => t.id === mention.id) ? prev : [...prev, mention]
    )
  }

  const handleMentionRemove = (mentionId: string) => {
    setTags(prev => prev.filter(t => t.id !== mentionId))
  }

  const handleInputChange = (text: string) => {
    setInputValue(text)
    // Prune tags that were deleted from the text area
    setTags(prev => prev.filter(t => text.includes(`@${t.target_username}`)))
  }

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return
    
    setIsLoading(true)
    
    try {
      // Create the conversation first, then navigate to its page
      const conversation = await chatService.createConversation(inputValue.trim())

      navigate(`/chat/${conversation.conversation_id}`, {
        state: { 
          initialMessage: inputValue.trim(),
          tags,
          status: 'notInitialized'
        }
      })
    } finally {
      setIsLoading(false)
      // Clear input and tags after navigation
      setInputValue('')
      setTags([])
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-blue-400/10 to-transparent dark:from-blue-400/5 rounded-full -translate-y-8 translate-x-8" />
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <CardTitle className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <MessageCircle className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                Chat
              </CardTitle>
              <p className="text-muted-foreground">Type "@" to mention target usernames from your analytics</p>
            </div>
            <Badge variant="secondary" className="text-xs">
              Interactive
            </Badge>
          </div>
        </CardHeader>
      </Card>

      {/* Chat Input */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-50/50 dark:bg-green-950/20 flex-shrink-0">
              <Send className="h-4 w-4 text-green-600 dark:text-green-400" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg font-semibold">Start a conversation</CardTitle>
              <CardDescription>Ask a question or brainstorm ideas with context from your saved items</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <MentionAutocomplete
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              onMentionAdd={handleMentionAdd}
              onMentionRemove={handleMentionRemove}
              placeholder="Type your message... Use @ to mention target usernames"
              rows={4}
              disabled={isLoading}
              className="w-full pr-16 py-4 pl-4 text-base leading-relaxed bg-background border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring transition-all"
            />
            <Button
              type="button"
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              className="absolute right-3 bottom-3 h-10 w-10 p-0 rounded-md"
            >
              {isLoading ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
} 